import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Product {
  id: string;
  name: string;
  barcode: string;
  min_price: number;
  store_count: number;
}

interface BranchPrice {
  price: number;
  product_initial_price: number;
  product_in_discount: boolean;
  branch_name: string;
  branch_code: string;
  company_name: string;
  city: string;
  data_source: string;
  wolt_delivery: boolean | null;
}

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [prices, setPrices] = useState<BranchPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedToList, setAddedToList] = useState(false);
  const [lists, setLists] = useState<any[]>([]);
  const [selectedList, setSelectedList] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/products/${id}`),
      api.get(`/products/${id}/prices`),
      api.get('/lists').catch(() => ({ data: [] })),
    ]).then(([pRes, pricesRes, listsRes]) => {
      setProduct(pRes.data);
      const pricesData = Array.isArray(pricesRes.data) ? pricesRes.data : [];
      setPrices(pricesData.sort((a: BranchPrice, b: BranchPrice) =>
        parseFloat(String(a.price)) - parseFloat(String(b.price))));
      setLists(listsRes.data);
      if (listsRes.data.length > 0) setSelectedList(listsRes.data[0].id);
    }).finally(() => setLoading(false));
  }, [id]);

  const addToList = async () => {
    if (!selectedList || !product) return;
    try {
      await api.post(`/lists/${selectedList}/items`, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
      });
      setAddedToList(true);
    } catch {
      alert('Please log in to add items to a list.');
    }
  };

  if (loading) return <div style={styles.center}>Φόρτωση...</div>;
  if (!product) return <div style={styles.center}>Το προϊόν δεν βρέθηκε.</div>;

  // Group by company, keep cheapest
  const byCompany: Record<string, BranchPrice> = {};
  prices.forEach(p => {
    const price = parseFloat(String(p.price));
    if (!byCompany[p.company_name] || price < parseFloat(String(byCompany[p.company_name].price))) {
      byCompany[p.company_name] = { ...p, price };
    }
  });
  const companySummary = Object.values(byCompany).sort((a, b) =>
    parseFloat(String(a.price)) - parseFloat(String(b.price)));
  const cheapest = companySummary[0];
  const mostExpensive = companySummary[companySummary.length - 1];

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.back}>← Πίσω</button>

      <div style={styles.hero}>
        <div style={styles.heroInfo}>
          <h1 style={styles.name}>{product.name}</h1>
          <p style={styles.available}>Διαθέσιμο σε {companySummary.length} αλυσίδες</p>
          {cheapest && mostExpensive && cheapest.company_name !== mostExpensive.company_name && (
            <div style={styles.savingsBanner}>
              💡 Αγόρασε από <strong>{cheapest.company_name}</strong> και εξοικονόμησε έως{' '}
              <strong>€{(parseFloat(String(mostExpensive.price)) - parseFloat(String(cheapest.price))).toFixed(2)}</strong>{' '}
              vs <strong>{mostExpensive.company_name}</strong>
            </div>
          )}
          <div style={styles.addRow}>
            {lists.length > 0 ? (
              <>
                <select value={selectedList} onChange={e => setSelectedList(e.target.value)} style={styles.select}>
                  {lists.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <button onClick={addToList} style={styles.addBtn} disabled={addedToList}>
                  {addedToList ? '✅ Προστέθηκε' : '+ Προσθήκη στη λίστα'}
                </button>
              </>
            ) : (
              <button onClick={() => navigate('/lists')} style={styles.addBtn}>
                + Δημιούργησε λίστα πρώτα
              </button>
            )}
          </div>
        </div>
      </div>

      <h2 style={styles.sectionTitle}>Τιμές ανά Αλυσίδα</h2>
      <div style={styles.priceList}>
        {companySummary.map((p, i) => (
          <div key={p.company_name} style={{ ...styles.priceRow, background: i === 0 ? '#EAFAF1' : 'white' }}>
            <div style={styles.priceLeft}>
              {i === 0 && <span style={styles.badge}>ΦΘΗΝΟΤΕΡΟ</span>}
              <div style={{display:'flex', alignItems:'center', gap: 6}}>
                <span style={styles.companyName}>{p.company_name}</span>
                {p.wolt_delivery && <span style={styles.woltBadge}>🛵 Wolt</span>}
              </div>
              <span style={styles.address}>{p.city}</span>
              <span style={styles.source}>{p.data_source}</span>
            </div>
            <span style={{ ...styles.priceTag, color: i === 0 ? '#27AE60' : '#2952CC' }}>
              €{parseFloat(String(p.price)).toFixed(2)}
              {p.product_in_discount && <span style={styles.offerTag}> ΠΡΟΣΦΟΡΑ</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 800, margin: '0 auto', padding: '24px 16px' },
  center: { textAlign: 'center', marginTop: 80, color: '#888' },
  back: { background: 'none', border: 'none', color: '#2952CC', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 },
  hero: { display: 'flex', gap: 24, marginBottom: 32, alignItems: 'flex-start', flexWrap: 'wrap' as const },
  heroInfo: { flex: 1, minWidth: 240 },
  name: { color: '#2952CC', fontSize: 22, fontWeight: 700, margin: '0 0 8px' },
  available: { color: '#5D6D7E', fontSize: 14, margin: '0 0 12px' },
  savingsBanner: { background: '#EAFAF1', border: '1px solid #A9DFBF', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#1E8449', marginBottom: 16 },
  addRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const },
  select: { padding: '8px 12px', borderRadius: 6, border: '1.5px solid #C5D5FF', fontSize: 14 },
  addBtn: { padding: '9px 18px', background: '#2952CC', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  sectionTitle: { color: '#2952CC', fontSize: 18, marginBottom: 12 },
  priceList: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  priceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderRadius: 8, border: '1px solid #E8EEF2' },
  priceLeft: { display: 'flex', flexDirection: 'column' as const, gap: 2 },
  badge: { fontSize: 10, fontWeight: 700, color: '#27AE60', letterSpacing: 1 },
  companyName: { fontWeight: 600, fontSize: 15, color: '#2952CC' },
  address: { fontSize: 12, color: '#888' },
  source: { fontSize: 11, color: '#aaa' },
  priceTag: { fontWeight: 700, fontSize: 18 },
  offerTag: { fontSize: 11, background: '#E74C3C', color: 'white', borderRadius: 4, padding: '2px 5px', marginLeft: 4 },
  woltBadge: { fontSize: 10, background: '#009DE0', color: 'white', borderRadius: 4, padding: '2px 6px', fontWeight: 600 },
};

export default ProductDetailPage;
